import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  Input,
  InputLabel,
  Select,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import * as Yup from "yup";
import { Formik } from "formik";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import { ArrowRightAltOutlined } from "@mui/icons-material";
import { updateReseachProject } from "app/services/researchProjectServices/ResearchProject";
import { MatxLoading } from "app/components";
import { getDivisions } from "app/services/divisionServices/Division";
import SnackbarProvider from "app/components/SnackbarProvider";
import { notify } from "app/utils/notifyUtils";

interface FormValues {
  id?: number;
  author: string;
  unit: string;
  title: string;
  funder: string;
  process: string;
  type: string;
  startDate: string;
  endDate: string;
  status: string;
  summary: string;
}

const validationSchema = Yup.object().shape({
  author: Yup.string().required("Autor é um campo obrigatório"),
  unit: Yup.string().required("Cidade é um campo obrigatório"),
  title: Yup.string().required("Titulo é um campo obrigatório"),
  funder: Yup.string().required("Financiador é um campo obrigatório"),
  process: Yup.string().required("Processo é um campo obrigatório"),
  type: Yup.string().required("Tipo é um campo obrigatório"),
  startDate: Yup.string().required("Data inícial é um campo obrigatório"),
  endDate: Yup.string().required("Data final é um campo obrigatório"),
  status: Yup.string().required("Situação é um campo obrigatório"),
  summary: Yup.string().required("Resumo é um campo obrigatório"),
});

function formatDate(dateTimeString) {
  const date = new Date(dateTimeString);
  const year = date.getFullYear();
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  return `${year}-${month}-${day}`;
}

interface FormProjectProps {
  data?: any;
  setNewProject?: any;
}

const FormProject: React.FC<FormProjectProps> = ({ data, setNewProject }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [divisionList, setDivisionList] = useState([]);
  const snackbarProvider = SnackbarProvider();

  const [initialValues, setInitialValues] = useState<FormValues>({
    author: "",
    unit: "",
    title: "",
    funder: "",
    process: "",
    type: "",
    startDate: "",
    endDate: "",
    status: "",
    summary: "",
  });

  const handleFormSubmit = async (values: any) => {
    setFormLoading(true);
    try {
      await updateReseachProject(values);
      notify("success", "Dados enviados com sucesso!");
    } catch (error) {
      notify("error", "Erro ao enviar dados. Tente novamente.");
    } finally {
      setFormLoading(false);
      setNewProject(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const loadUserContactInfo = async () => {
      const userData = data;
      if (userData) {
        setInitialValues({
          id: userData.id,
          author: userData.author,
          unit: userData.division_id,
          title: userData.title,
          funder: userData.financier,
          process: userData.proccess,
          type: userData.type,
          startDate: formatDate(userData.initial_date),
          endDate: formatDate(userData.end_date),
          status: userData.status,
          summary: userData.summary,
        });
      }

      const divisions = await getDivisions();
      setDivisionList(divisions.data.response);
      setLoading(false);
    };
    loadUserContactInfo();
  }, []); // eslint-disable-line

  if (loading) {
    return (
      <div style={{ height: "10vh" }}>
        <MatxLoading />
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 1,
        width: "100%",
        mx: "auto",
      }}
    >
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Input type="hidden" value={values.id} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  name="title"
                  label="Titulo"
                  variant="outlined"
                  margin="none"
                  onBlur={handleBlur}
                  value={values.title}
                  onChange={handleChange}
                  helperText={touched.title && errors.title}
                  error={Boolean(errors.title && touched.title)}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  name="author"
                  label="Autor"
                  variant="outlined"
                  margin="none"
                  onBlur={handleBlur}
                  value={values.author}
                  onChange={handleChange}
                  helperText={touched.author && errors.author}
                  error={Boolean(errors.author && touched.author)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="process"
                  label="N° Processo"
                  variant="outlined"
                  margin="none"
                  onBlur={handleBlur}
                  value={values.process}
                  onChange={handleChange}
                  helperText={touched.process && errors.process}
                  error={Boolean(errors.process && touched.process)}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  name="funder"
                  label="Financiador"
                  variant="outlined"
                  margin="dense"
                  onBlur={handleBlur}
                  value={values.funder}
                  onChange={handleChange}
                  helperText={touched.funder && errors.funder}
                  error={Boolean(errors.funder && touched.funder)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="type"
                  label="Tipo"
                  variant="outlined"
                  margin="dense"
                  onBlur={handleBlur}
                  value={values.type}
                  onChange={handleChange}
                  helperText={touched.type && errors.type}
                  error={Boolean(errors.type && touched.type)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={Boolean(errors.unit && touched.unit)}
                >
                  <Autocomplete
                    options={divisionList}
                    value={
                      divisionList.find(
                        (item: any) => item.id === values.unit
                      ) || null
                    }
                    getOptionLabel={(option: any) =>
                      `${option.institution.name} - ${option.name}`
                    }
                    onChange={(event, newValue) => {
                      handleChange({
                        target: {
                          name: "unit",
                          value: newValue ? newValue.id : "",
                        },
                      });
                    }}
                    onBlur={handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cidade"
                        variant="outlined"
                        name="unit"
                        value={values.unit}
                        error={Boolean(errors.unit && touched.unit)}
                        helperText={
                          touched.unit && errors.unit ? errors.unit : ""
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={Boolean(errors.status && touched.status)}
                >
                  <InputLabel> Situação </InputLabel>
                  <Select
                    label="Situação"
                    size="medium"
                    name="status"
                    variant="outlined"
                    margin="dense"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">
                      <em>Selecione...</em>
                    </MenuItem>
                    <MenuItem value="Vigente"> Vigente </MenuItem>
                    <MenuItem value="Finalizado"> Finalizado </MenuItem>
                  </Select>
                  {touched.status && errors.status && (
                    <FormHelperText style={{ color: "red" }}>
                      {errors.status}{" "}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="startDate"
                  type="date"
                  label="Data Inícial"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  onBlur={handleBlur}
                  value={values.startDate}
                  onChange={handleChange}
                  helperText={touched.startDate && errors.startDate}
                  error={Boolean(errors.startDate && touched.startDate)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="endDate"
                  type="date"
                  variant="outlined"
                  label="Data Final"
                  InputLabelProps={{ shrink: true }}
                  onBlur={handleBlur}
                  value={values.endDate}
                  onChange={handleChange}
                  helperText={touched.endDate && errors.endDate}
                  error={Boolean(errors.endDate && touched.endDate)}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  name="summary"
                  label="Resumo"
                  variant="outlined"
                  margin="none"
                  onBlur={handleBlur}
                  value={values.summary}
                  onChange={handleChange}
                  multiline
                  rows={5}
                  helperText={touched.summary && errors.summary}
                  error={Boolean(errors.summary && touched.summary)}
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <LoadingButton
                  type="submit"
                  color="primary"
                  loading={formLoading}
                  variant="contained"
                  endIcon={<ArrowRightAltOutlined fontSize="small" />}
                >
                  Cadastrar
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default FormProject;
